/**
 * useConnections (SYN-1520) — the /workspace/connections surface over the
 * Command BFF: composed list (`GET /api/workspace/connections`), the
 * disconnect / configure-access mutations, and the spec-shaped capability
 * lifecycle calls the add-connection wizard makes
 * (`POST /api/v1/capabilities/:id/lifecycle/(start|oauth_consent|register|test)`).
 *
 * Mirrors useApprovals: Clerk JWT auth, fail-closed parsing, refetch-driven
 * truth (mutations never optimistically flip a card's auth health — the list
 * always reflects what the BFF composed from Coherence/Atlas state).
 *
 * SYN-1729: AbortController timeout + one automatic retry so a stalled
 * upstream never leaves the UI in an indefinite skeleton state. On timeout
 * the status transitions to "slow" (retry banner); on second failure it
 * settles at "unavailable".
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";

import { createApiHeaders } from "../lib/api";
import { APPS_CHANGED_EVENT, announceAppsChanged } from "../lib/apps-changed";
import {
  parseConnectionsPayload,
  type ConnectionCard,
  type ConnectionRegistration,
  type ConnectionTargetResolution,
} from "../lib/connections";

const CONNECTIONS_API = "/api/workspace/connections";

/** Milliseconds before a fetch is considered stalled (SYN-1729). */
const FETCH_TIMEOUT_MS = 15_000;

export type ConnectionsStatus =
  | "loading"
  | "ready"
  | "unauthenticated"
  | "forbidden"
  | "unavailable"
  /** Fetch timed out; a retry is in progress or the user may retry manually. */
  | "slow";

export interface LifecycleCallResult {
  ok: boolean;
  status: number;
  output: unknown;
  message: string | null;
  /**
   * The parsed response body, verbatim.
   *
   * 🔴 SYN-2627. `ok` and the HTTP status are NOT enough to say a destructive action
   * happened. `lifecycleBody` (src/api/handlers/connections.ts) returns the lifecycle
   * `status` alongside `ok`, and `ok` is computed as `status !== "denied" && status !==
   * "failed"` (src/coherence/lifecycle-audit.ts:473) — so `approval_requested` (HTTP 202)
   * and `recorded` (a dry run) both arrive `ok: true` on a 2xx. Only `applied` means the
   * credential was actually revoked. A caller that renders `result.ok` as "done" tells the
   * customer their connection is gone when it may only be queued.
   *
   * `output` cannot carry that verdict: it is `payload.output ?? payload`, so a non-null
   * tool output REPLACES the envelope and the lifecycle status disappears. The refusal
   * shapes (`connectionRefs` on a 409) are lost the same way. Hence the raw body.
   */
  body: Record<string, unknown>;
}

export interface UseConnectionsResult {
  connections: ConnectionCard[];
  registrations: ConnectionRegistration[];
  errors: Record<string, string | null>;
  target: ConnectionTargetResolution | null;
  status: ConnectionsStatus;
  error: string | null;
  refetch: () => Promise<void>;
  /** Ask Coherence to select the declared setup flow for the exact catalog ID. */
  startAuthorization: (
    connectionId: string,
    input?: Record<string, unknown>,
  ) => Promise<LifecycleCallResult>;
  /** Wizard authenticate (guided API-key entry): POST .../lifecycle/register. */
  registerKey: (ref: string, input: Record<string, unknown>) => Promise<LifecycleCallResult>;
  /** Wizard verify: POST .../lifecycle/test. */
  verify: (ref: string) => Promise<LifecycleCallResult>;
  /** Destructive; reason required (the only free-text field on this surface). */
  disconnect: (ref: string, reason: string) => Promise<LifecycleCallResult>;
  /**
   * SYN-3316 (Maria, 3 Oct 2026): ONE Remove. Disconnects and deletes a connection its maker
   * made, in one step. Answers in the disconnect envelope plus `removed` / `stillConnected`.
   */
  remove: (ref: string, reason: string) => Promise<LifecycleCallResult>;
  listPendingDisconnects: () => Promise<LifecycleCallResult>;
  checkDisconnectStatus: (connectionId: string) => Promise<LifecycleCallResult>;
  configureAccess: (
    ref: string,
    scope: "workspace" | "roles",
    roleKeys: string[],
  ) => Promise<LifecycleCallResult>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Fetch with a hard timeout backed by AbortController (SYN-1729).
 * Rejects with a `DOMException` named "TimeoutError" when the deadline fires.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException("Timed out", "TimeoutError")),
    timeoutMs,
  );
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export function useConnections(
  connectionId?: string | null,
  readScope: "workspace" | "own" = "workspace",
): UseConnectionsResult {
  const readUrl = readScope === "own" ? `${CONNECTIONS_API}/own` : CONNECTIONS_API;
  const { getToken, orgId, userId } = useAuth();
  const [connections, setConnections] = useState<ConnectionCard[]>([]);
  const [registrations, setRegistrations] = useState<ConnectionRegistration[]>([]);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [target, setTarget] = useState<ConnectionTargetResolution | null>(null);
  const [status, setStatus] = useState<ConnectionsStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  // Track in-flight refetch to prevent overlapping calls on mount + manual retry.
  const fetchingRef = useRef(false);
  const inFlightConnectionIdRef = useRef<string | null>(null);
  const queuedTargetRef = useRef(false);
  const requestedConnectionIdRef = useRef(connectionId ?? null);
  requestedConnectionIdRef.current = connectionId ?? null;

  const buildHeaders = useCallback(async () => {
    const token = await getToken({ template: "command-session" });
    return createApiHeaders({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
  }, [getToken]);

  /**
   * Single-attempt fetch. Returns false when it should be retried (timeout),
   * true when it either succeeded or produced a terminal error.
   */
  const attemptFetch = useCallback(
    async (requestedConnectionId: string | null, isRetry: boolean): Promise<boolean> => {
      try {
        const res = await fetchWithTimeout(
          requestedConnectionId
            ? `${readUrl}?connection_id=${encodeURIComponent(requestedConnectionId)}`
            : readUrl,
          { headers: await buildHeaders() },
          FETCH_TIMEOUT_MS,
        );
        if (requestedConnectionIdRef.current !== requestedConnectionId) {
          return true;
        }
        if (res.status === 401 || res.status === 403) {
          setConnections([]);
          setRegistrations([]);
          setTarget(null);
          setStatus(res.status === 401 ? "unauthenticated" : "forbidden");
          return true;
        }
        if (!res.ok) {
          setConnections([]);
          setRegistrations([]);
          setTarget(null);
          setStatus("unavailable");
          setError(`Connections service error (${res.status})`);
          return true;
        }
        const payload = parseConnectionsPayload(await res.json().catch(() => null));
        setConnections(payload.connections);
        setRegistrations(payload.registrations);
        setErrors(payload.errors);
        setTarget(payload.target);
        setStatus("ready");
        return true;
      } catch (err) {
        if (requestedConnectionIdRef.current !== requestedConnectionId) {
          return true;
        }
        const isTimeout = err instanceof DOMException && err.name === "TimeoutError";
        if (isTimeout && !isRetry) {
          // First timeout: surface "slow" and signal the caller to retry.
          setStatus("slow");
          return false;
        }
        // Network error or second timeout: terminal failure.
        setConnections([]);
        setRegistrations([]);
        setTarget(null);
        setStatus("unavailable");
        setError(err instanceof Error ? err.message : String(err));
        return true;
      }
    },
    [buildHeaders, readUrl],
  );

  const refetch = useCallback(async () => {
    const requestedConnectionId = requestedConnectionIdRef.current;
    if (fetchingRef.current) {
      if (inFlightConnectionIdRef.current !== requestedConnectionId) {
        queuedTargetRef.current = true;
      }
      return;
    }
    fetchingRef.current = true;
    try {
      let targetConnectionId: string | null;
      do {
        queuedTargetRef.current = false;
        targetConnectionId = requestedConnectionIdRef.current;
        inFlightConnectionIdRef.current = targetConnectionId;
        setStatus("loading");
        setError(null);
        const done = await attemptFetch(targetConnectionId, false);
        if (!done && requestedConnectionIdRef.current === targetConnectionId) {
          // First attempt timed out ("slow") — one automatic retry.
          await attemptFetch(targetConnectionId, true);
        }
      } while (queuedTargetRef.current || requestedConnectionIdRef.current !== targetConnectionId);
    } finally {
      inFlightConnectionIdRef.current = null;
      fetchingRef.current = false;
    }
  }, [attemptFetch]);

  const call = useCallback(
    async (
      path: string,
      body?: Record<string, unknown>,
      method: "GET" | "POST" = "POST",
    ): Promise<LifecycleCallResult> => {
      try {
        const res = await fetch(path, {
          method,
          headers: await buildHeaders(),
          body: method === "POST" ? JSON.stringify(body) : undefined,
        });
        const payload = asRecord(await res.json().catch(() => null));
        return {
          ok: res.ok && payload.ok !== false,
          status: res.status,
          output: payload.output ?? payload,
          message: typeof payload.message === "string" ? payload.message : null,
          body: payload,
        };
      } catch {
        return {
          ok: false,
          status: 0,
          output: null,
          message: "That didn't go through. Try again in a moment.",
          body: {},
        };
      }
    },
    [buildHeaders],
  );

  const lifecyclePath = (ref: string, action: string) =>
    `/api/v1/capabilities/${encodeURIComponent(ref)}/lifecycle/${action}`;

  const startAuthorization = useCallback(
    (targetConnectionId: string, input: Record<string, unknown> = {}) =>
      call(lifecyclePath(targetConnectionId, "start"), { input }),
    [call],
  );
  const registerKey = useCallback(
    (ref: string, input: Record<string, unknown>) =>
      call(lifecyclePath(ref, "register"), { input }),
    [call],
  );
  const verify = useCallback((ref: string) => call(lifecyclePath(ref, "test"), {}), [call]);

  const checkDisconnectStatus = useCallback(
    (connectionId: string) =>
      call(
        `/api/connections/${encodeURIComponent(connectionId)}/disconnect-status`,
        undefined,
        "GET",
      ),
    [call],
  );

  const listPendingDisconnects = useCallback(
    () => call("/api/connections/disconnect-status", undefined, "GET"),
    [call, orgId, userId],
  );

  const disconnect = useCallback(
    async (ref: string, reason: string) => {
      const result = await call(`${CONNECTIONS_API}/${encodeURIComponent(ref)}/disconnect`, {
        reason,
      });
      // Keep the pending dialog mounted; it refreshes the list when closed.
      if (result.ok && result.body.status !== "disconnect_pending") {
        await refetch();
        announceAppsChanged();
      }
      return result;
    },
    [call, refetch],
  );

  const remove = useCallback(
    async (ref: string, reason: string) => {
      const result = await call(`${CONNECTIONS_API}/${encodeURIComponent(ref)}/remove`, {
        reason,
      });
      // As disconnect: a pending removal keeps its dialog mounted; it refreshes when closed.
      if (result.ok && result.body.status !== "disconnect_pending") {
        await refetch();
        // Every other list of her apps (the menu tree) re-reads too.
        announceAppsChanged();
      }
      return result;
    },
    [call, refetch],
  );

  const configureAccess = useCallback(
    async (ref: string, scope: "workspace" | "roles", roleKeys: string[]) => {
      const result = await call(`${CONNECTIONS_API}/${encodeURIComponent(ref)}/access`, {
        scope,
        roleKeys,
      });
      if (result.ok) {
        await refetch();
      }
      return result;
    },
    [call, refetch],
  );

  useEffect(() => {
    void refetch();
  }, [connectionId, refetch]);

  useEffect(() => {
    const refreshApps = () => {
      void refetch();
    };
    window.addEventListener(APPS_CHANGED_EVENT, refreshApps);
    return () => window.removeEventListener(APPS_CHANGED_EVENT, refreshApps);
  }, [refetch]);

  return {
    connections,
    registrations,
    errors,
    target,
    status,
    error,
    refetch,
    startAuthorization,
    registerKey,
    verify,
    disconnect,
    remove,
    checkDisconnectStatus,
    listPendingDisconnects,
    configureAccess,
  };
}
